import { changePassword, changePasswordWithVerification } from "@actions/profile";
import { showAlert } from "@components/alert/action";
import { useVerification } from "@layouts/v4/verificationPopup/index.js";
import { useStrongPasswordValidator } from "@utils/hooks.js";
import { Button, Form } from "antd";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { connect } from "react-redux";
import FloatingLabelInput from "../../components/FloatingLabelInput";
import { ChangePasswordHeader, ChangePasswordWrapper } from "./elements";

const ChangePassword = ({
	translate,
	changePassword,
	changePasswordWithVerification,
	showAlert,
	user,
}) => {
	const [form] = Form.useForm();
	const [isFormEmpty, setIsFormEmpty] = useState({
		PreviousPassword: true,
		ProposedPassword: true,
		confirm_password: true,
	});
	const username = user?.username;
	const { withVerification } = useVerification();

	const handleChangePassword = async (values) => {
		if (window.SUMSUB_VERIFICATION_INTERCEPTOR && withVerification) {
			const changePasswordPromise = (params) =>
				new Promise((resolve, reject) =>
					changePasswordWithVerification(params, resolve, reject)
				);

			try {
				await withVerification(changePasswordPromise, values);

				form.resetFields();

				showAlert({
					type: "success",
					msg: "PASSWORD_UPDATED",
					title: "SUCCESSFULL_TITLE",
				});
			} catch (error) {
				if (error?.message && error.message.includes("verification.closed")) {
					return;
				}

				if (error?.message && error.message.includes("verification")) {
					showAlert({
						type: "error",
						msg: error.message,
						title: "ERROR",
					});
					return;
				}

				showAlert({
					type: "error",
					msg: "GENERIC_ERROR",
					title: "ERROR",
				});
			}

			return;
		}

		changePassword(username, values, () => {
			form.resetFields();
		});
	};

	const handleValuesChange = (changedValues, allValues) => {
		setIsFormEmpty({
			PreviousPassword: !allValues.PreviousPassword || allValues.PreviousPassword === "",
			ProposedPassword: !allValues.ProposedPassword || allValues.ProposedPassword === "",
			confirm_password: !allValues.confirm_password || allValues.confirm_password === "",
		});
		form.validateFields(Object.keys(changedValues));
	};

	const validatePasswords = ({ getFieldValue }) => ({
		validator(_, value) {
			if (!value || getFieldValue("ProposedPassword") === value) {
				return Promise.resolve();
			}
			return Promise.reject(new Error(translate("NOT_MATCH_PASS")));
		},
	});

	const isAnyFieldEmpty = (formEmptyObject) => {
		return Object.values(formEmptyObject).some((value) => value);
	};

	const strongPasswordValidator = useStrongPasswordValidator(translate);

	return (
		<ChangePasswordWrapper>
			<ChangePasswordHeader>{translate("Change Password")}</ChangePasswordHeader>
			<Form
				onFinish={handleChangePassword}
				onValuesChange={handleValuesChange}
				form={form}>
				<Form.Item
					key={"PreviousPassword"}
					name="PreviousPassword"
					rules={[
						{
							required: true,
							message: translate("Please input your current password!"),
						},
					]}>
					<FloatingLabelInput
						key={"PreviousPassword"}
						label={translate("Current Password")}
						isEmpty={isFormEmpty.PreviousPassword}
					/>
				</Form.Item>
				<Form.Item
					key={"ProposedPassword"}
					name="ProposedPassword"
					rules={[
						{ required: true, message: translate("PLEASE_INPUT_YOUR_PASS") },
						...(strongPasswordValidator
							? [{ validator: strongPasswordValidator }]
							: []),
					]}>
					<FloatingLabelInput
						key={"ProposedPassword"}
						label={translate("New Password")}
						isEmpty={isFormEmpty.ProposedPassword}
					/>
				</Form.Item>
				<Form.Item
					key={"confirm_password"}
					name="confirm_password"
					rules={[validatePasswords]}>
					<FloatingLabelInput
						key={"confirm_password"}
						label={translate("Confirm New Password")}
						isEmpty={isFormEmpty.confirm_password}
					/>
				</Form.Item>
				<Form.Item>
					<Button
						className="change-password-button"
						type="primary"
						htmlType="submit"
						disabled={isAnyFieldEmpty(isFormEmpty)}
						style={{ opacity: isAnyFieldEmpty(isFormEmpty) ? "0.5" : "1" }}>
						{translate("CHANGE_PASSWORD")}
					</Button>
				</Form.Item>
			</Form>
		</ChangePasswordWrapper>
	);
};

ChangePassword.propTypes = {
	translate: PropTypes.func.isRequired,
	changePassword: PropTypes.func.isRequired,
	changePasswordWithVerification: PropTypes.func.isRequired,
	showAlert: PropTypes.func.isRequired,
	user: PropTypes.object,
};

const mapDispatchToProps = { changePassword, changePasswordWithVerification, showAlert };
export default connect(null, mapDispatchToProps)(ChangePassword);
